import { useEffect, useMemo, useState } from 'react';

import { api } from '../api/endpoints';
import type { EventCard, PersonDetail } from '../api/types';
import { LOOKING_FOR_LABELS } from '../api/types';
import { EventCardView } from '../components/EventCardView';
import { formatEventDate } from '../format';

interface Props {
  onOpenEvent: (id: number) => void;
}

type FieldKind = 'text' | 'area' | 'number' | 'list';

interface Field {
  key: keyof PersonDetail;
  label: string;
  kind: FieldKind;
  hint?: string;
}

/** Те же восемь вопросов, что задаёт бот, плюс поля нетворкинга.
 *  Порядок повторяет анкету: человек ищет глазами то, что уже отвечал. */
const PUBLIC_FIELDS: Field[] = [
  { key: 'full_name', label: 'Имя и фамилия', kind: 'text' },
  { key: 'occupation', label: 'Ниша бизнеса', kind: 'text', hint: 'Недвижимость, IT, строительство' },
  { key: 'company', label: 'Компания', kind: 'text' },
  {
    key: 'fun_fact',
    label: 'Интересный факт о вас',
    kind: 'area',
    hint: 'То, чего не угадаешь по нише. С этого начинают разговор на встрече',
  },
  { key: 'website', label: 'Сайт или соцсеть', kind: 'text' },
  {
    key: 'interests',
    label: 'Интересы',
    kind: 'list',
    hint: 'Через запятую: сёрф, инвестиции, вино',
  },
];

const NETWORKING_FIELDS: Field[] = [
  {
    key: 'looking_for',
    label: 'Что ищу',
    kind: 'area',
    hint: 'Своими словами — по этому тексту вас находят в поиске',
  },
  { key: 'can_offer', label: 'Чем могу быть полезен', kind: 'area' },
  {
    key: 'offer',
    label: 'Продающий оффер',
    kind: 'area',
    hint: 'Короткое предложение для рекомендаций внутри сообщества',
  },
];

const PRIVATE_FIELDS: Field[] = [
  { key: 'age', label: 'Возраст', kind: 'number' },
  { key: 'contacts', label: 'Связь', kind: 'text', hint: 'Telegram или WhatsApp' },
  { key: 'monthly_income_usd', label: 'Доход в месяц, $', kind: 'text', hint: '5–7к' },
  { key: 'employees_count', label: 'Сотрудников', kind: 'text' },
];

const ALL_FIELDS = [...PUBLIC_FIELDS, ...NETWORKING_FIELDS, ...PRIVATE_FIELDS];

/** В форме всё живёт строками: пустая строка и null различаются только
 *  в момент отправки, и смешивать типы в состоянии — лишний источник багов. */
type Draft = Record<string, string>;

function toDraft(profile: PersonDetail): Draft {
  const draft: Draft = {};
  for (const field of ALL_FIELDS) {
    const value = profile[field.key];
    draft[field.key] =
      value == null ? '' : Array.isArray(value) ? value.join(', ') : String(value);
  }
  return draft;
}

function parse(field: Field, raw: string): unknown {
  const value = raw.trim();
  if (field.kind === 'list') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }
  if (field.kind === 'number') {
    return value ? Number(value) : null;
  }
  return value || null;
}

export function ProfilePage({ onOpenEvent }: Props) {
  const [me, setMe] = useState<PersonDetail | null>(null);
  const [past, setPast] = useState<EventCard[]>([]);
  const [draft, setDraft] = useState<Draft>({});
  const [tags, setTags] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api
      .me()
      .then((profile) => {
        setMe(profile);
        setDraft(toDraft(profile));
        setTags(profile.looking_for_tags);
      })
      .catch(() => setMissing(true));
    api
      .myEvents('past')
      .then(setPast)
      .catch(() => setPast([]));
  }, []);

  const dirty = useMemo(() => {
    if (!me) return false;
    const original = toDraft(me);
    const tagsChanged =
      tags.length !== me.looking_for_tags.length ||
      tags.some((tag) => !me.looking_for_tags.includes(tag));
    return tagsChanged || ALL_FIELDS.some((f) => draft[f.key] !== original[f.key]);
  }, [me, draft, tags]);

  function set(key: string, value: string) {
    setDraft((current) => ({ ...current, [key]: value }));
    setSaved(false);
    setError(null);
  }

  function toggleTag(tag: string) {
    setTags((current) =>
      current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag],
    );
    setSaved(false);
  }

  async function save() {
    if (!me) return;
    setSaving(true);
    setError(null);
    try {
      // Отправляем только изменённое: PATCH на бэкенде идёт с
      // exclude_unset, и присланное поле затирает то, что там было.
      const original = toDraft(me);
      const patch: Record<string, unknown> = {};
      for (const field of ALL_FIELDS) {
        if (draft[field.key] !== original[field.key]) {
          patch[field.key] = parse(field, draft[field.key] ?? '');
        }
      }
      const tagsChanged =
        tags.length !== me.looking_for_tags.length ||
        tags.some((tag) => !me.looking_for_tags.includes(tag));
      if (tagsChanged) patch.looking_for_tags = tags;

      const updated = await api.updateMe(patch);
      setMe(updated);
      setDraft(toDraft(updated));
      setTags(updated.looking_for_tags);
      setSaved(true);
    } catch {
      setError('Не сохранилось. Проверьте связь и попробуйте ещё раз.');
    } finally {
      setSaving(false);
    }
  }

  if (missing) {
    return (
      <div className="screen">
        <h1 className="screen__title">Мой профиль</h1>
        <p className="muted">
          Профиль ещё не создан. Откройте бота и заполните анкету — команда
          /anketa.
        </p>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="screen">
        <h1 className="screen__title">Мой профиль</h1>
        <div className="skeleton" />
      </div>
    );
  }

  const renderField = (field: Field) => (
    <label key={field.key} className="field">
      <span className="field__label">{field.label}</span>
      {field.kind === 'area' ? (
        <textarea
          className="field__input field__input--area"
          rows={3}
          value={draft[field.key] ?? ''}
          onChange={(event) => set(field.key, event.target.value)}
        />
      ) : (
        <input
          className="field__input"
          inputMode={field.kind === 'number' ? 'numeric' : 'text'}
          value={draft[field.key] ?? ''}
          onChange={(event) => set(field.key, event.target.value)}
        />
      )}
      {field.hint && <span className="field__hint">{field.hint}</span>}
    </label>
  );

  return (
    <div className="screen">
      <h1 className="screen__title">{me.full_name}</h1>

      <section className="block">
        <h2 className="block__title">Карточка</h2>
        <p className="muted">Её видят участники мероприятий, на которые вы идёте.</p>
        {PUBLIC_FIELDS.map(renderField)}
      </section>

      <section className="block">
        <h2 className="block__title">Нетворкинг</h2>
        <p className="muted">По этим полям вас находят через поиск по участникам.</p>

        <span className="field__label">Что ищу</span>
        <div className="picks">
          {Object.entries(LOOKING_FOR_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`pick${tags.includes(key) ? ' pick--on' : ''}`}
              onClick={() => toggleTag(key)}
            >
              {label}
            </button>
          ))}
        </div>

        {NETWORKING_FIELDS.map(renderField)}
      </section>

      <section className="block">
        <h2 className="block__title">Только для вас</h2>
        <p className="muted">
          Эти данные не видны другим участникам — они нужны для отбора в клуб
          и чтобы с вами могли связаться организаторы.
        </p>
        {PRIVATE_FIELDS.map(renderField)}
      </section>

      {/* Кнопка неактивна, пока ничего не изменили: иначе непонятно,
          сохранилось ли, и её жмут по второму разу. */}
      <div className="savebar">
        {error && <span className="savebar__error">{error}</span>}
        <button className="btn" onClick={save} disabled={saving || !dirty}>
          {saving ? 'Сохраняю…' : saved && !dirty ? 'Сохранено' : 'Сохранить'}
        </button>
      </div>

      {past.length > 0 && (
        <section className="block">
          <h2 className="block__title">Я был</h2>
          <div className="cards">
            {past.slice(0, 10).map((event) => (
              <EventCardView key={event.id} event={event} onOpen={onOpenEvent} />
            ))}
          </div>
        </section>
      )}

      {past.length === 0 && (
        <p className="muted">
          История участия появится здесь после первого мероприятия.
        </p>
      )}

      <p className="muted small">
        Анкету можно перезаполнить целиком в боте командой /anketa.
        {past[0] && ` Последнее мероприятие: ${formatEventDate(past[0].starts_at)}.`}
      </p>
    </div>
  );
}
